import { useDebounce } from '@shared/hooks'

import { useFiltersContext } from '@/context'
import { SortBy, useTicketFiltersContext } from '@/context/filters/ticketFilters/ticketFilters'
import { TicketQueries } from '@/graphql/queries/ticket.queries'
import { TicketFilterParams, TicketListRow } from '@/types/Ticket'
import { formatDate } from '@/util'

import { useGraphql } from '../useGraphql'

const useTickets = () => {
  const {
    endingCreatedAtDate,
    priority,
    ticketReasons,
    ticketStatuses,
    ticketTypes,
    ticketAssigneeId,
    searchString,
    sortBy,
    startingCreatedAtDate,
  } = useTicketFiltersContext()
  const [{ marketSegmentFilter }] = useFiltersContext()

  const debouncedSearch = useDebounce(searchString, 1000)
  const start = startingCreatedAtDate
  const end = endingCreatedAtDate

  const createdAt =
    start || end
      ? { start: start && formatDate(start, 'yyyy-MM-dd'), end: end && formatDate(end, 'yyyy-MM-dd') }
      : undefined

  const filterParams: Partial<TicketFilterParams> = {
    marketSegmentIds: marketSegmentFilter.marketSegmentIds,
    search: debouncedSearch || undefined,
    createdAt,
    includeInternalTickets: true,
    priority,
    ticketReasons,
    ticketStatuses,
    ticketTypes,
    ticketAssigneeId,
  }
  const ticketsQuery = useGraphql<{ tickets: TicketListRow[] }>(
    TicketQueries.QUERY_KEYS.getTickets,
    TicketQueries.getTickets(),
    {
      filter: filterParams,
      sortBy: sortBy
        ? {
            field: getSortByField(sortBy),
            direction: sortBy.desc ? 'Descending' : 'Ascending',
          }
        : null,
    },
    { refetchOnMount: 'always' },
  )
  return ticketsQuery
}

function getSortByField(sortBy: SortBy) {
  switch (sortBy.id) {
    case 'id': {
      return 'TicketId'
    }
    case 'marketSegment.name': {
      return 'MarketSegment'
    }
    case 'internalTicketStatus': {
      return 'TicketStatus'
    }
    case 'priority': {
      return 'Priority'
    }
    case 'createdAt': {
      return 'CreatedAt'
    }
    case 'ticketType': {
      return 'TicketType'
    }
    case 'lastCommentDatetime': {
      return 'LastCommentDatetime'
    }
    default: {
      return
    }
  }
}

export default useTickets
